import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUpload } from '../../contexts/UploadContext';
import { APP_VERSION } from '../../version';
import './Layout.css';

const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { hasActiveUploads, canLogout } = useUpload();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);

    const handleLogout = async () => {
        if (!canLogout()) {
            setShowLogoutWarning(true);
            return;
        }
        await logout();
        navigate('/login');
    };



    const navItems = [
        { path: '/', label: 'Dashboard', icon: 'fa-th-large', section: 'operator' },
        { path: '/recording', label: 'Record Video', icon: 'fa-video', section: 'operator' },
        { path: '/feeds', label: 'My Feeds', icon: 'fa-list', section: 'operator' },
        { path: '/uploading', label: 'Uploading', icon: 'fa-upload', section: 'operator' },
        { path: '/admin/users', label: 'User Management', icon: 'fa-users', section: 'admin' },
        { path: '/admin/feeds', label: 'All Feeds', icon: 'fa-server', section: 'admin' },
        { path: '/admin/failed', label: 'Failed Uploads', icon: 'fa-exclamation-triangle', section: 'admin' },
        { path: '/admin/uploading', label: 'All Uploading', icon: 'fa-cloud-upload-alt', section: 'admin' },
    ];

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    {!sidebarOpen && (
                        /* Collapsed state — orange cat mascot only (same art as the favicon) */
                        <img
                            src="/favicon/favicon-dark.png"
                            alt="Salty"
                            className="sidebar-cat-logo"
                        />
                    )}
                    <div className="logo">
                        {/* Salty wordmark — inline SVG cropped tight to letterforms incl. y descender */}
                        <svg className="sidebar-logo-img" viewBox="120 180 640 275" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#DC5829" d="M405.85,232.74h-51.22c-.7,0-1.27.57-1.27,1.27v10.59c0,1.14-1.37,1.7-2.17.89-10.17-10.12-22.89-16.09-36.7-16.09-24.25,0-45.15,18.39-54.74,45.4-7.52-30.96-35.42-45.5-67.08-45.5-44.53,0-67.22,23.84-67.22,49.41,0,61.48,86.18,39.36,86.18,60.61,0,6.89-6.03,11.78-18.1,11.78-13.24,0-22.9-7.46-26.51-18.05-.12-.36-.47-.6-.85-.57l-42.11,3.6c-.49.04-.84.5-.75.98,6.01,33.85,30.09,49.66,71.38,49.66,38.67,0,62.39-17.11,66.12-42.27,10.01,25.48,30.29,42.61,53.69,42.61,13.81,0,26.53-5.97,36.7-16.09.81-.8,2.17-.24,2.17.89v10.27c0,.7.57,1.27,1.27,1.27h51.22c.7,0,1.27-.57,1.27-1.27v-148.12c0-.7-.57-1.27-1.27-1.27ZM254.28,313.64c-20.55-27.89-79.15-17.23-79.15-36.66,0-6.61,5.46-11.78,16.66-11.78,13.28,0,21.56,6.12,25.38,19.17.11.38.47.63.86.6l39.51-3.12c-2.2,8.22-3.41,17.09-3.41,26.38,0,1.82.06,3.62.15,5.41ZM323.5,337.13c-12.82,0-23.22-12.77-23.22-28.53s10.39-29.6,23.22-29.6,23.22,13.84,23.22,29.6-10.39,28.53-23.22,28.53Z" />
                            <path fill="#DC5829" d="M753.03,233.31h-49.21c-.85,0-1.61.56-1.86,1.37l-25.54,82.49c-.54,1.75-2.97,1.85-3.66.16l-33.65-82.8c-.3-.73-1.01-1.21-1.8-1.21h-66.78c-1.24,0-2.25-1.01-2.25-2.25v-42.05c0-1.24-1.01-2.25-2.25-2.25h-41.5c-1.11,0-2.06.81-2.22,1.91l-2.87,19.06c-2.87,19.25-9.77,27-30.45,29.3l-3.45.36c-1.14.12-2.01,1.08-2.01,2.23v30.78c0,1.24,1.01,2.25,2.25,2.25h28.54c1.24,0,2.25,1.01,2.25,2.25v46.3c0,41.94,20.68,65.5,56.02,65.5,12.04,0,24.08-1.83,33.15-5.72.81-.35,1.33-1.17,1.33-2.05v-38.8c0-1.57-1.58-2.66-3.04-2.09-5.34,2.08-11.39,3.28-17.64,3.28-10.92,0-18.1-6.03-18.1-17.81v-48.6c0-1.24,1.01-2.25,2.25-2.25h30.26l39.89,89.15s-.05,0-.08,0l8.24,18.41c.21.48.23,1.02.04,1.51-6.62,16.83-17.36,20.19-28.66,20.21-4.44,0-9.08-.43-13.76-1.27-.16-.03-.31-.03-.46-.02-1.04.02-1.9.88-1.89,1.97l.52,37.69c.01.92.66,1.7,1.56,1.88,6.35,1.25,12.63,1.99,20.87,1.99.01,0,.03,0,.04,0,.01,0,.03,0,.04,0,31.03,0,54.58-12.07,71.24-54.87l2.59-6.61,53.91-146.77c.47-1.27-.47-2.61-1.83-2.61Z" />
                            <path fill="#DC5829" d="M507.85,337.55c-5.55,2.29-11.92,3.63-18.5,3.63-10.92,0-18.1-6.03-18.1-17.81v-135.17c0-.87-.71-1.58-1.58-1.58h-47.69c-1.11,0-2.06.81-2.22,1.91l-.23,86.23v46.3c0,41.94,20.68,65.5,56.02,65.5,12.22,0,24.44-1.88,33.55-5.9.57-.25.93-.82.93-1.44v-40.23c0-1.12-1.14-1.88-2.18-1.45Z" />
                        </svg>
                        <div className="sidebar-logo-divider" />
                        <span className="sidebar-logo-product">VMS</span>
                    </div>
                    <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                        <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'}`}></i>
                    </button>
                </div>

                <nav className="sidebar-nav">
                    <div className="nav-section">
                        <span className="nav-section-title">Menu</span>
                        {navItems.filter(item => item.section === 'operator').map(item => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            >
                                <span className="nav-icon"><i className={`fas ${item.icon}`}></i></span>
                                <span className="nav-label">{item.label}</span>
                            </NavLink>
                        ))}
                    </div>

                    {user?.role === 'admin' && (
                        <div className="nav-section">
                            <span className="nav-section-title">Admin</span>
                            {navItems.filter(item => item.section === 'admin').map(item => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                >
                                    <span className="nav-icon"><i className={`fas ${item.icon}`}></i></span>
                                    <span className="nav-label">{item.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <button
                        className={`sidebar-logout-btn ${hasActiveUploads ? 'disabled' : ''}`}
                        onClick={handleLogout}
                        title={hasActiveUploads ? "Cannot logout during active upload" : "Logout"}
                        disabled={hasActiveUploads}
                    >
                        <i className="fas fa-sign-out-alt"></i>
                        <span className="nav-label">Logout</span>
                    </button>
                    <div className="sidebar-user-info">
                        <div className="sidebar-user-avatar">
                            {user?.username?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                            <span className="user-name">{user?.username || user?.email?.split('@')[0]}</span>
                            <span className="user-role">{user?.role}</span>
                        </div>
                    </div>
                    <span className="sidebar-version">v{APP_VERSION}</span>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <header className="top-header">
                    {hasActiveUploads && (
                        <div className="upload-indicator">
                            <span className="upload-dot"></span>
                            Upload in progress
                        </div>
                    )}
                    <div className="header-user-info">
                        <div className="header-user-details">
                            <span className="header-email">{user?.email || user?.username}</span>
                            <span className="header-user-role">{user?.role}</span>
                        </div>
                        <button
                            className={`logout-btn ${hasActiveUploads ? 'disabled' : ''}`}
                            onClick={handleLogout}
                            title={hasActiveUploads ? "Cannot logout during active upload" : "Logout"}
                        >
                            <i className="fas fa-sign-out-alt"></i>
                        </button>
                    </div>
                </header>
                <div className="content-area">
                    {children}
                </div>
            </main>

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="modal-overlay" onClick={() => setShowLogoutWarning(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Upload in Progress</h2>
                        </div>
                        <div className="modal-body">
                            <p>You have active uploads in progress. Please wait for them to complete before logging out.</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowLogoutWarning(false)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;
